import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Skeletons, not spinners, for anything with a known shape (§10.6).
 * A spinner says "wait"; a skeleton says "this is what is coming".
 */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('skeleton h-4 w-full', className)} />;
}

export function TableSkeleton({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="divide-y divide-line" aria-busy aria-label="Yuklanmoqda">
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="grid items-center gap-4 px-4 py-3.5" style={{ gridTemplateColumns: `2fr repeat(${cols - 1}, 1fr)` }}>
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} className={c === 0 ? 'h-4 w-3/4' : 'h-3 w-16'} />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Every list has a designed empty state with one action, not an empty table
 * (§10.5). Empty screens invite action; they do not explain absence.
 */
export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-card bg-brand-50 text-brand-500 dark:bg-brand-500/12 dark:text-brand-300">
        {icon}
      </div>
      <h3 className="font-display text-section text-strong">{title}</h3>
      {hint && <p className="mt-2 max-w-[36ch] text-body text-muted">{hint}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

/**
 * Row-level state, not a legend (§10.6). A published row carries a red dot;
 * a draft is neutral. No colour key to memorise.
 *
 * On the public site the red dot means iPost. Here it means: this is on the
 * real website.
 */
export function LiveDot({ live, className }: { live: boolean; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block h-2 w-2 shrink-0 rounded-chip',
        live ? 'bg-accent-500' : 'border border-faint bg-transparent',
        className,
      )}
    />
  );
}

export function StatusChip({ live, labels }: { live: boolean; labels: { live: string; draft: string } }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-chip px-2 py-0.5 text-eyebrow uppercase',
        live
          ? 'bg-accent-50 text-accent-700 dark:bg-accent-500/12 dark:text-accent-300'
          : 'bg-raised text-muted',
      )}
    >
      <LiveDot live={live} />
      {live ? labels.live : labels.draft}
    </span>
  );
}

/** An inline notice. Amber is soft warnings; never red, which is reserved. */
export function Notice({
  tone = 'info',
  title,
  children,
  action,
}: {
  tone?: 'info' | 'warn' | 'danger';
  title?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  const tones = {
    info: 'border-brand-200 bg-brand-50 text-brand-900 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-100',
    warn: 'border-amber-400/40 bg-amber-400/10 text-amber-600 dark:text-amber-400',
    danger: 'border-accent-200 bg-accent-50 text-accent-700 dark:border-accent-500/30 dark:bg-accent-500/10 dark:text-accent-300',
  };
  return (
    <div className={cn('flex items-start justify-between gap-4 rounded-card border px-4 py-3', tones[tone])}>
      <div className="text-ui leading-relaxed">
        {title && <p className="mb-0.5 font-medium">{title}</p>}
        {children}
      </div>
      {action}
    </div>
  );
}

/**
 * The save line: a 1px brand bar under the header while a request is in
 * flight. No spinner and no toast for autosave — a toast every 30 seconds is
 * noise, and noise is what people learn to ignore (§10.5).
 */
export function SaveLine({ active }: { active: boolean }) {
  return (
    <div aria-hidden className="relative h-px w-full overflow-hidden bg-line">
      {active && <div className="absolute inset-y-0 left-0 w-1/4 animate-save-bar bg-brand-500" />}
    </div>
  );
}
